import { FC, memo, useCallback, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import { AccordionSection } from "../../../components/AccordionSection";
import { LightTooltip } from "@/components/LightTooltip";
import {
  useCompanyFilterActions,
  useCompanyFilterOptions,
  useCompanyFilterSlice,
} from "../../../hooks";
import type { IntentOptions } from "../options";
import {
  BUYERS_JOURNEY_TOOLTIP_SECTIONS,
  INTENT_CONTEXT_TOOLTIP_SECTIONS,
  type TooltipSection,
} from "../constants";

const TooltipContent: FC<{ sections: TooltipSection[]; maxWidth: number }> =
  memo(({ sections, maxWidth }) => {
    return (
      <Box sx={{ maxWidth }}>
        {sections.map((section, idx) => (
          <Box key={`${section.title}-${idx}`} sx={{ mt: idx === 0 ? 0 : 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {section.title}
            </Typography>
            {section.description && (
              <Typography variant="body2" color="text.secondary">
                {section.description}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  });

TooltipContent.displayName = "TooltipContent";

const toggleId = (ids: string[], id: string, checked: boolean): string[] => {
  return checked ? [...ids, id] : ids.filter((x) => x !== id);
};

export const BuyerContextSection: FC = memo(() => {
  const slice = useCompanyFilterSlice("intent");
  const { setSlice } = useCompanyFilterActions();
  const intentOptions = useCompanyFilterOptions("intent") as
    | IntentOptions
    | undefined;

  const [expanded, setExpanded] = useState(false);

  const hasAnySelection =
    slice.buyersJourneyIds.length > 0 || slice.contextTypeIds.length > 0;

  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setSlice("intent", {
        ...slice,
        buyersJourneyIds: [],
        contextTypeIds: [],
      });
    },
    [setSlice, slice]
  );

  const handleJourneyToggle = useCallback(
    (journeyId: string, checked: boolean) => {
      const newIds = toggleId(slice.buyersJourneyIds, journeyId, checked);
      setSlice("intent", { ...slice, buyersJourneyIds: newIds });
    },
    [slice, setSlice]
  );

  const handleContextToggle = useCallback(
    (contextId: string, checked: boolean) => {
      const newIds = toggleId(slice.contextTypeIds, contextId, checked);
      setSlice("intent", { ...slice, contextTypeIds: newIds });
    },
    [slice, setSlice]
  );

  return (
    <AccordionSection
      title="Buyer Context"
      expanded={expanded}
      onToggle={() => setExpanded((prev) => !prev)}
      action={
        hasAnySelection ? (
          <Tooltip title="Reset">
            <IconButton
              size="small"
              aria-label="Reset Buyer Context"
              onClick={handleClear}
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null
      }
    >
      <Stack spacing={2}>
        {intentOptions?.buyersJourneyOptions &&
          intentOptions.buyersJourneyOptions.length > 0 && (
            <Box>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2">
                  Buyer&apos;s Journey
                </Typography>
                <LightTooltip
                  placement="right"
                  title={
                    <TooltipContent
                      sections={BUYERS_JOURNEY_TOOLTIP_SECTIONS}
                      maxWidth={300}
                    />
                  }
                >
                  <IconButton
                    size="small"
                    aria-label="Buyer's journey help"
                    disableRipple
                    sx={{ p: 0.25 }}
                  >
                    <InfoOutlinedIcon
                      fontSize="inherit"
                      sx={{ fontSize: 16 }}
                    />
                  </IconButton>
                </LightTooltip>
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  columnGap: 2,
                  rowGap: 0.25,
                }}
              >
                {intentOptions.buyersJourneyOptions.map((journey) => (
                  <FormControlLabel
                    key={journey.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={slice.buyersJourneyIds.includes(journey.id)}
                        onChange={(e) =>
                          handleJourneyToggle(journey.id, e.target.checked)
                        }
                      />
                    }
                    label={
                      <Typography variant="body2">{journey.name}</Typography>
                    }
                    sx={{
                      mr: 0,
                      "& .MuiFormControlLabel-label": { ml: 0.5 },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

        {intentOptions?.contextTypeOptions &&
          intentOptions.contextTypeOptions.length > 0 && (
            <Box>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2">Intent Context</Typography>
                <LightTooltip
                  placement="right"
                  title={
                    <TooltipContent
                      sections={INTENT_CONTEXT_TOOLTIP_SECTIONS}
                      maxWidth={320}
                    />
                  }
                >
                  <IconButton
                    size="small"
                    aria-label="Intent context help"
                    disableRipple
                    sx={{ p: 0.25 }}
                  >
                    <InfoOutlinedIcon
                      fontSize="inherit"
                      sx={{ fontSize: 16 }}
                    />
                  </IconButton>
                </LightTooltip>
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  columnGap: 2,
                  rowGap: 0.5,
                }}
              >
                {intentOptions.contextTypeOptions.map((ctx) => (
                  <FormControlLabel
                    key={ctx.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={slice.contextTypeIds.includes(ctx.id)}
                        onChange={(e) =>
                          handleContextToggle(ctx.id, e.target.checked)
                        }
                        sx={{ py: 0.25 }}
                      />
                    }
                    label={<Typography variant="body2">{ctx.name}</Typography>}
                    sx={{
                      mr: 0,
                      my: 0,
                      "& .MuiFormControlLabel-label": { ml: 0.5 },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
      </Stack>
    </AccordionSection>
  );
});

BuyerContextSection.displayName = "BuyerContextSection";
